import { parseISO } from 'date-fns';

// Shared ticket-link helpers — the single implementation for anything that renders
// or opens ticket URLs (EventHero, TicketPanel). Ticket refs are the eventMapper
// shape: `{ url, provider }` (or a bare URL string; getUrlString normalizes).
//
//   openTicketUrl(eventId, url)     open a ticket link AND fire the buy_ticket
//                                   interaction (authed) / anonymous tracking —
//                                   never window.open a ticket URL directly
//   getTicketPlatformLabel(ref)     "Ticketmaster" / root domain / null
//   getOccurrenceTicketRefs(occ)    ticket refs of one occurrence
//   dedupeTicketRefs(refs)          drop refs with duplicate URLs
//   getFirstAvailableOccurrence(os) default occurrence pick (future + has tickets)
//   formatOccurrenceDate(date,time,t,locale) "Sat, Jun 20, 2099 at 20:00"
import { recordEventInteraction } from './eventInteractions';
import { trackBuyTicket } from './anonymousTracker';
import { getTodayKey } from './dateKey';

const KNOWN_PLATFORMS = {
  ticketmaster: 'Ticketmaster',
  seetickets: 'See Tickets',
  axs: 'AXS',
  eventbrite: 'Eventbrite',
  wegow: 'Wegow',
  enterticket: 'EnterTicket',
  ticketea: 'Ticketea',
  entradas: 'Entradas.com',
  viagogo: 'Viagogo',
  stubhub: 'StubHub',
  livenation: 'Live Nation',
  fever: 'Fever',
  taquilla: 'Taquilla.com',
  atrapalo: 'Atrápalo',
};

// Two-part TLDs where the root domain is the 3rd-from-last part
const TWO_PART_TLDS = new Set([
  'co.uk', 'co.es', 'com.es', 'gob.es', 'org.es', 'net.es',
  'com.ar', 'co.ar', 'com.mx', 'co.mx', 'gob.mx',
  'com.br', 'org.br', 'com.au', 'co.nz',
]);

const MAX_DOMAIN_LENGTH = 25;

export const getUrlString = (urlOrRef) => {
  if (typeof urlOrRef === 'string') return urlOrRef;
  if (urlOrRef && typeof urlOrRef.url === 'string') return urlOrRef.url;
  return null;
};

// Lowercased hostname for a URL string or ref, or null if it can't be parsed.
const getHostname = (urlOrRef) => {
  const url = getUrlString(urlOrRef);
  if (!url) return null;
  try {
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(normalized).hostname.toLowerCase();
  } catch {
    return null;
  }
};

// A ticket ref we never surface on web: one an admin marked bad (dead/stale). The API keeps such
// refs in the event doc (so ingestion can't lose the marker) but they must never be shown as a
// ticket option. Blocked providers are no longer part of this test — that policy is admin-managed
// in Postgres and the API strips those refs from the payload (`drop_blocked_url_refs`), so a
// blocked link never reaches the browser at all.
// Exported so eventMapper's primary-URL pick uses the exact same suppression rule as the
// render-time filters below (one chokepoint — a hidden ref can't sneak in as "the" ticket link).
export const isHiddenTicketRef = (urlOrRef) =>
  !!(urlOrRef && typeof urlOrRef === 'object' && urlOrRef.bad === true);

export const getTicketPlatformLabel = (urlOrRef) => {
  if (urlOrRef && typeof urlOrRef === 'object') {
    if (urlOrRef.provider && KNOWN_PLATFORMS[urlOrRef.provider]) {
      return KNOWN_PLATFORMS[urlOrRef.provider];
    }

    if (typeof urlOrRef.provider === 'string' && urlOrRef.provider.includes('.')) {
      return urlOrRef.provider;
    }
  }

  const hostname = getHostname(urlOrRef);
  if (!hostname) return null;

  // Reject bare IPs — not a recognizable platform name
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return null;

  // Strip leading www.
  const clean = hostname.replace(/^www\./, '');
  const parts = clean.split('.');

  // Need at least domain + TLD
  if (parts.length < 2) return null;

  // Determine root domain, accounting for two-part TLDs (e.g. gob.es, co.uk)
  const lastTwo = parts.slice(-2).join('.');
  const rootDomain = TWO_PART_TLDS.has(lastTwo) && parts.length >= 3
    ? parts.slice(-3).join('.')
    : lastTwo;

  const baseName = rootDomain.split('.')[0];

  if (KNOWN_PLATFORMS[baseName]) return KNOWN_PLATFORMS[baseName];

  if (rootDomain.length <= MAX_DOMAIN_LENGTH) return rootDomain;

  // Long domain (e.g. ayuntamientodevalencia.es) — generic fallback
  return 'official site';
};

export const getOccurrenceTicketRef = (occurrence) => {
  if (!occurrence) return null;
  if (occurrence.ticketUrlRef) return occurrence.ticketUrlRef;
  if (Array.isArray(occurrence.urls) && occurrence.urls[0]) return occurrence.urls[0];
  return null;
};

export const getOccurrenceTicketRefs = (occurrence) => {
  if (!occurrence) return [];
  if (Array.isArray(occurrence.urls) && occurrence.urls.length > 0) {
    return occurrence.urls.filter((ref) => !isHiddenTicketRef(ref));
  }

  const ticketRef = getOccurrenceTicketRef(occurrence);
  return ticketRef && !isHiddenTicketRef(ticketRef) ? [ticketRef] : [];
};

export const dedupeTicketRefs = (ticketRefs = []) => {
  const seenUrls = new Set();

  return ticketRefs.filter((ticketRef) => {
    if (isHiddenTicketRef(ticketRef)) return false;
    const url = getUrlString(ticketRef);
    if (!url || seenUrls.has(url)) return false;
    seenUrls.add(url);
    return true;
  });
};


// Occurrences from today onward, in the caller's order. Anything the page labels
// (a date range, a picker, a badge) reads from this, not from the raw array. The
// API filters the same way (filter_public_occurrences), so this is what keeps a
// cached or pre-upgrade payload from labelling a date nobody can attend.
export const getFutureOccurrences = (occurrences = []) => {
  if (!Array.isArray(occurrences)) return [];
  // Compared as strings, never as Dates: `new Date('2026-08-18')` is UTC midnight
  // while a local `new Date()` is local midnight, so anywhere west of UTC today's
  // showing counted as past — dropped from the picker, tickets unreachable.
  const todayKey = getTodayKey();
  return occurrences.filter((occurrence) => (occurrence?.local_date || '') >= todayKey);
};

export const getFirstAvailableOccurrence = (occurrences = []) => {
  if (!Array.isArray(occurrences) || occurrences.length === 0) return null;

  const futureOccurrences = getFutureOccurrences(occurrences);

  const futureWithTickets = futureOccurrences.find((occurrence) => getOccurrenceTicketRefs(occurrence).length > 0);
  if (futureWithTickets) return futureWithTickets;

  if (futureOccurrences.length > 0) return futureOccurrences[0];

  return occurrences.find((occurrence) => getOccurrenceTicketRefs(occurrence).length > 0) || occurrences[0];
};

// "Sat, Jun 20, 2099 at 20:00" / "sáb, 20 jun 2099 a las 20:00". `t` is the caller's
// translator and `locale` the app's language (`i18n.language`).
// One date and one time formatter per locale: the panel formats every listed showing.
const occurrenceFormats = new Map();
const occurrenceFormatsFor = (locale) => {
  if (!occurrenceFormats.has(locale)) {
    occurrenceFormats.set(locale, {
      date: new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      time: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }),
    });
  }
  return occurrenceFormats.get(locale);
};

export const formatOccurrenceDate = (dateString, timeString, t, locale) => {
  const formats = occurrenceFormatsFor(locale);
  // parseISO, not `new Date`: a bare 'YYYY-MM-DD' parses as UTC midnight and
  // renders as the previous day for any viewer west of UTC.
  const dateFormatted = formats.date.format(parseISO(dateString));

  if (timeString) {
    const timeFormatted = formats.time.format(new Date(`2000-01-01T${timeString}`));
    return t('event:shareDateTime', { date: dateFormatted, time: timeFormatted });
  }

  return dateFormatted;
};

// Open the URL, then fire the buy_ticket interaction (authenticated) or track anonymously.
// Opening first keeps the window inside the click: after an awaited request Safari blocks
// it as a popup. `noopener` keeps the provider's page from reaching back into this tab.
export const openTicketUrl = (eventId, url) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener');
  recordEventInteraction(eventId, 'buy_ticket', trackBuyTicket);
};
